import { supabase } from '../../../lib/supabase';
import { AlfaDocsService, type AlfaDocsConfig } from '../../../lib/services/alfadocsService';
import { getAlfaDocsLinks, getPatientIdForPractice, getPrimaryPracticeId } from '../../../lib/services/alfadocsLinks';

// === AlfaDocs multi-studio: risoluzione write-back per studio (Task 13) ===
// Fonte: LeadStatusUpdateDrawer.tsx:38-77 (porting letterale, Task 6). Semantica
// identica a resolveClientAlfaConfig in src/lib/stores/calendarStore.ts: creazione
// FUORI dalla tab Clinica → practice PRIMARIA del cliente.
//
// Convenzione tipi (Task 16, spec §6.5): il `type` persistito su `sales_activities` per
// un appuntamento AlfaDocs è SEMPRE lo standard `'appuntamento'` — mai `alfadocs_*`. Il
// sottotipo (visita/operazione/telehealth) vive SOLO in `metadata.alfadocs_type`. Le
// funzioni di QUESTO modulo non leggono/scrivono mai `type`: operano su `alfaType`
// (passato esplicitamente dal chiamante, v. `resolveAlfaDocsSubType` in
// `activityPayload.ts`) e su `metadata.alfadocs_practice_id`/`alfadocs_appointment_id` —
// sono perciò già agnostiche rispetto a QUALE convenzione ha scritto il `type` della riga
// (retrocompat automatica per le attività storiche `alfadocs_*` in lettura/update).

/**
 * Risolve la connessione AlfaDocs + patientId per la creazione di un NUOVO
 * appuntamento a partire dal cliente (practice primaria del cliente).
 * Fonte: LeadStatusUpdateDrawer.tsx:41-57.
 */
export async function resolveClientAlfaConfig(
  clientId: string,
  organizationId: string
): Promise<{ config: AlfaDocsConfig; patientId: string } | null> {
  const { data: clientInfo } = await supabase.from('clients').select('metadata').eq('id', clientId).single();
  const configs = (await AlfaDocsService.listConfigs(organizationId)).filter(c => c.is_active);
  if (configs.length === 0) return null;
  const oldestPracticeId = configs[0]?.configuration?.practice_id ?? null;
  const primary = getPrimaryPracticeId(clientInfo?.metadata, oldestPracticeId);
  if (!primary) return null;
  const links = getAlfaDocsLinks(clientInfo?.metadata, oldestPracticeId);
  const cfg = configs.find(c => c.configuration?.practice_id === primary)
    || configs.find(c => links[c.configuration?.practice_id || '']) || null;
  if (!cfg || !cfg.configuration?.practice_id) return null;
  const patientId = getPatientIdForPractice(clientInfo?.metadata, cfg.configuration.practice_id, oldestPracticeId);
  return patientId ? { config: cfg, patientId } : null;
}

/**
 * Risolve la connessione AlfaDocs per il write-back di un appuntamento GIÀ ESISTENTE:
 * metadata.alfadocs_practice_id (practice dell'APPUNTAMENTO) → fallback primaria cliente →
 * fallback connessione attiva più vecchia dell'org.
 * Fonte: LeadStatusUpdateDrawer.tsx:62-77. Usata dal ramo UPDATE (`syncAlfaDocsUpdate` in
 * `useActivitySave.ts`, dal Task 9). Indipendente dal `type` dell'attività (legacy
 * `alfadocs_*` o canonico `appuntamento`, v. Task 16): instrada solo sulla
 * `practice_id` in metadata, mai sul tipo — nessuna modifica richiesta qui per la
 * retrocompat delle due convenzioni.
 */
export async function resolveActivityAlfaConfig(
  practiceIdHint: string | null | undefined,
  clientId: string | undefined,
  organizationId: string
): Promise<AlfaDocsConfig | null> {
  const configs = (await AlfaDocsService.listConfigs(organizationId)).filter(c => c.is_active);
  if (configs.length === 0) return null;
  const oldestPracticeId = configs[0]?.configuration?.practice_id ?? null;
  let practiceId = practiceIdHint || undefined;
  if (!practiceId && clientId) {
    const { data: clientInfo } = await supabase.from('clients').select('metadata').eq('id', clientId).single();
    practiceId = getPrimaryPracticeId(clientInfo?.metadata, oldestPracticeId) || undefined;
  }
  if (!practiceId) practiceId = oldestPracticeId || undefined;
  return configs.find(c => c.configuration?.practice_id === practiceId) || configs[0] || null;
}

/**
 * Toast minimale richiesto dal modulo (sottoinsieme di react-hot-toast: `toast(...)` per
 * gli avvisi informativi, `toast.error(...)` per l'errore di sync). Tipizzato invece di
 * `any` per restare fedele alla firma di piano pur dando un minimo di sicurezza ai chiamanti.
 */
export interface AlfaDocsToast {
  (message: string, opts?: { icon?: string; duration?: number }): void;
  error(message: string): void;
}

export interface SyncAlfaDocsAppointmentInput {
  clientId: string;
  activityId: string;
  /**
   * Sottotipo AlfaDocs (`visita`, `operazione`, `telehealth`, ...), derivato dal tipo RAW
   * scelto in UI (v. `resolveAlfaDocsSubType` in `activityPayload.ts`). `null` → l'attività
   * NON è un appuntamento AlfaDocs, la funzione è un no-op. A differenza della Fase 1, qui
   * NON arriva più il `safeType` normalizzato: dalla convenzione canonica (Task 16) quel
   * valore è sempre `'appuntamento'` e non porterebbe più il sottotipo.
   */
  alfaType: string | null;
  title: string;
  startDateTime: Date;
  endDateTime: Date | null;
  /**
   * DEVIAZIONE dalla firma di piano (segnalata in task-6-report.md): la fonte risolve
   * `organizationId` da `userProfile.data.organization_id` (utente loggato) PRIMA di
   * entrare nel blocco AlfaDocs — è indispensabile per `resolveClientAlfaConfig` e per
   * `AlfaDocsService.createAppointment`, quindi non può restare implicito nel modulo
   * (che non ha accesso a hook/store). Il chiamante deve risolverlo e passarlo.
   */
  organizationId: string;
  toast: AlfaDocsToast;
}

/**
 * Sincronizza (best-effort) la creazione di un appuntamento AlfaDocs quando l'attività
 * appena inserita è un appuntamento AlfaDocs (`alfaType` valorizzato). MAI blocca il
 * submit del chiamante: qualunque esito (cliente non collegato, colore non configurato,
 * errore API) termina con un `return` normale — a differenza della fonte, che a
 * `:1474-1477` faceva `return` dentro l'handler del submit dell'intero drawer
 * (interrompendo anche i passi successivi, es. la sync Google/Microsoft Calendar). Qui il
 * `return` termina SOLO questa funzione; il chiamante prosegue sempre col resto del flusso.
 *
 * Fonte: LeadStatusUpdateDrawer.tsx:1463-1510 (blocco INSERT, convenzione `type='alfadocs_*'`).
 *
 * CONVENZIONE CANONICA (Task 16, spec §6.5): il `type` persistito su `sales_activities`
 * NON è più `alfadocs_*` (quello era solo il tipo RAW scelto in UI) — è sempre lo standard
 * `'appuntamento'` (v. `resolveSafeType` in `activityPayload.ts`), come già faceva il
 * vecchio drawer DEAL pre-refactor (`StatusUpdateDrawer.tsx`, blocco `isAlfaAppt`). Questa
 * funzione riceve perciò `alfaType` già estratto dal chiamante (`resolveAlfaDocsSubType`)
 * invece del `safeType` — che ormai non porterebbe più il sottotipo.
 */
export async function syncAlfaDocsAppointment(input: SyncAlfaDocsAppointmentInput): Promise<void> {
  const { clientId, activityId, alfaType, title, startDateTime, endDateTime, organizationId, toast } = input;

  // Fonte :1465 — gating esterno al try, replicato qui: fuori scope AlfaDocs, non facciamo nulla.
  if (!alfaType || !organizationId) return;

  try {
    const resolved = await resolveClientAlfaConfig(clientId, organizationId);
    if (!resolved) {
      toast('Questo cliente non è collegato ad AlfaDocs. L\'attività è stata salvata solo sul CRM.', { icon: '⚠️', duration: 5000 });
      return;
    }

    const endTime = endDateTime || new Date(startDateTime.getTime() + 30 * 60000);
    const durationMin = Math.round((endTime.getTime() - startDateTime.getTime()) / 60000);
    const apptColorId = resolved.config.configuration?.appointments_config?.default_color_id;
    if (!apptColorId) {
      toast('Colore appuntamento non configurato. Vai in Impostazioni → AlfaDocs → Appuntamenti per selezionare un colore.', { icon: '⚠️', duration: 6000 });
      // Fonte :1476: `return` che nel drawer originale interrompeva l'intero submit.
      // Qui termina solo la sync AlfaDocs — il chiamante prosegue normalmente.
      return;
    }

    // Formato locale per AlfaDocs (no timezone — AlfaDocs interpreta come orario locale).
    const pad = (n: number) => String(n).padStart(2, '0');
    const alfaDate = `${startDateTime.getFullYear()}-${pad(startDateTime.getMonth() + 1)}-${pad(startDateTime.getDate())} ${pad(startDateTime.getHours())}:${pad(startDateTime.getMinutes())}:${pad(startDateTime.getSeconds())}`;

    const res = await AlfaDocsService.createAppointment(organizationId, {
      date: alfaDate,
      patientId: parseInt(resolved.patientId),
      duration: durationMin,
      description: title,
      type: alfaType === 'telehealth' ? 'telehealth' : 'inPractice',
      colorId: apptColorId,
    }, { configId: resolved.config.id });

    if (res?.data?.id) {
      const practiceId = resolved.config.configuration?.practice_id;
      const { error: metaError } = await supabase.from('sales_activities')
        .update({ metadata: { alfadocs_appointment_id: String(res.data.id), alfadocs_type: alfaType, alfadocs_practice_id: practiceId } })
        .eq('id', activityId);
      if (metaError) {
        console.error('[syncAlfaDocsAppointment] Failed to save AlfaDocs metadata:', metaError);
      } else {
        console.log('[syncAlfaDocsAppointment] AlfaDocs appointment created and metadata saved:', res.data.id);
      }
    } else {
      console.warn('[syncAlfaDocsAppointment] AlfaDocs createAppointment returned no id:', res);
    }
  } catch (alfaErr) {
    console.warn('[syncAlfaDocsAppointment] AlfaDocs sync failed:', alfaErr);
    toast.error('Errore nella sincronizzazione con AlfaDocs. L\'attività è stata salvata solo sul CRM.');
  }
}
